import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import ErrorIcon from '@material-ui/icons/Error';

/**
 * The shape RJSF hands an `ErrorListTemplate`, reduced to what is read here.
 * Declared locally so this plugin does not take `@rjsf/utils` as a dependency
 * for two fields.
 */
export interface StepError {
  /** `.domains` for a field-level error; absent for a form-level one. */
  property?: string;
  message?: string;
  stack: string;
}

export interface StepErrorListProps {
  errors: StepError[];
  /** The schema of the step being shown, not of the whole template. */
  schema: { properties?: Record<string, unknown> };
}

const useStyles = makeStyles(() =>
  createStyles({
    list: { width: '100%' },
    text: { textWrap: 'wrap' },
  }),
);

/** The step property an error is about, or undefined for a form-level error. */
export function errorProperty(error: StepError): string | undefined {
  if (!error.property) return undefined;
  const path = error.property.startsWith('.')
    ? error.property.substring(1)
    : error.property;
  return path.split('.')[0] || undefined;
}

/**
 * The errors of a step that belong on the step being shown.
 *
 * The wizard keeps the last validation result until the next "Next", and
 * hands it to every step's form as `extraErrors`. So after the Domain step
 * refuses a host name, "Back" shows the Integrations step with the Domain
 * step's error at the top — about a field that is not on the page. An error
 * about a field is shown where that field is; an error about nothing in
 * particular is shown wherever it was raised.
 */
export function errorsForStep(
  errors: StepError[],
  schema: StepErrorListProps['schema'],
): StepError[] {
  const fields = schema.properties ?? {};
  return errors.filter(error => {
    const property = errorProperty(error);
    return property === undefined || property in fields;
  });
}

/**
 * Backstage's own error list, minus the errors of other steps.
 *
 * Same markup and wording as `@backstage/plugin-scaffolder-react`'s
 * `ErrorListTemplate`, so nothing else on the page changes: an error about a
 * field is prefixed with that field's title.
 */
export const StepErrorListTemplate = ({
  errors,
  schema,
}: StepErrorListProps) => {
  const classes = useStyles();
  const shown = errorsForStep(errors, schema);
  if (!shown.length) return null;

  const format = (error: StepError) => {
    const property = errorProperty(error);
    if (property && error.message) {
      const field = schema.properties?.[property];
      const title =
        field && typeof field === 'object' && 'title' in field
          ? String((field as { title: unknown }).title)
          : property;
      return `'${title}' ${error.message}`;
    }
    return error.stack;
  };

  return (
    <Paper>
      <List dense className={classes.list}>
        {shown.map((error, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              <ErrorIcon color="error" />
            </ListItemIcon>
            <ListItemText
              classes={{ primary: classes.text }}
              primary={format(error)}
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};
